/**
 * MAIN-848: client-side helpers for the menu item image upload.
 *
 * The image now goes through the hardened `uploadMenuItemImage` mutation
 * instead of a direct signed-URL PUT, so the server validates, resizes and
 * strips metadata. These helpers exist to fail fast in the browser — telling a
 * caterer their 30 MB screenshot is too big before it is base64-encoded and
 * sent is a much better experience than waiting for a server rejection — and
 * to keep that logic unit-testable, since this repo has no React DOM testing
 * library.
 *
 * Limits deliberately mirror the server (api/src/modules/menu/menuItemImage.js).
 * The server remains the authority; this is only a fast pre-check.
 */

export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateSelectedImageFile(file) {
  if (!file) {
    return { ok: false, message: "No image was selected." };
  }
  if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
    return {
      ok: false,
      message: "Choose a JPEG, PNG or WebP image.",
    };
  }
  if (typeof file.size === "number" && file.size > MAX_IMAGE_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      message: `That image is ${mb} MB. Please choose one under 5 MB.`,
    };
  }
  return { ok: true };
}

/**
 * Strip the `data:<mime>;base64,` prefix a FileReader result carries — the
 * mutation takes raw base64, and sending the prefix would corrupt the decoded
 * buffer on the server.
 */
export function stripDataUrlPrefix(dataUrl) {
  if (typeof dataUrl !== "string") return "";
  const comma = dataUrl.indexOf(",");
  return dataUrl.startsWith("data:") && comma !== -1
    ? dataUrl.slice(comma + 1)
    : dataUrl;
}

/** Read a File into raw base64 for the GraphQL mutation. */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(stripDataUrlPrefix(reader.result));
    reader.onerror = () =>
      reject(new Error("That image could not be read. It may be corrupt."));
    reader.readAsDataURL(file);
  });
}

/**
 * What the form should submit for the image, as one object.
 *
 * Three states, and the distinction between the last two is what stops a
 * caterer editing a price and silently losing their photo:
 *   1. a new upload  -> write both the URL and the metadata the server derived
 *                       for it, replacing whatever was there before;
 *   2. an explicit remove -> clear both (empty string / null, never undefined,
 *                       so updateMenu actually receives the change);
 *   3. untouched     -> emit nothing at all, so a partial update leaves the
 *                       existing image and its metadata — including a legacy
 *                       plain-string itemImage with no metadata — exactly as
 *                       they are.
 *
 * Metadata is taken straight from the uploadMenuItemImage response; nothing is
 * reconstructed on the client.
 *
 * @returns {{ itemImage?: string, itemImageMeta?: object|null }}
 */
export function resolveSubmittedItemImage({ uploadedUrl, uploadedMeta, isImageDelete }) {
  if (uploadedUrl) {
    return {
      itemImage: uploadedUrl,
      itemImageMeta: buildItemImageMeta(uploadedMeta),
    };
  }
  if (isImageDelete) return { itemImage: "", itemImageMeta: null };
  return {};
}

/**
 * Reshape an uploadMenuItemImage response into the MenuItemImageMetaInput the
 * mutation accepts. `url` is carried on itemImage itself, and __typename would
 * be rejected by the input type, so both are dropped.
 */
export function buildItemImageMeta(uploaded) {
  if (!uploaded) return null;
  const meta = {
    source: uploaded.source ?? null,
    mimeType: uploaded.mimeType ?? null,
    sizeBytes: uploaded.sizeBytes ?? null,
    width: uploaded.width ?? null,
    height: uploaded.height ?? null,
    originalFilename: uploaded.originalFilename ?? null,
    uploadedAt: uploaded.uploadedAt ?? null,
  };
  // An all-empty object is worse than none — it would overwrite good metadata.
  return Object.values(meta).some((v) => v !== null && v !== undefined)
    ? meta
    : null;
}
